import type { HelpTopic } from '../types';

export const estimatesDesignsTopics: HelpTopic[] = [
  {
    id: 'wms.estimates.create',
    title: 'Create an Estimate',
    icon: 'ri-file-list-3-line',
    summary: 'Build a detailed estimate with BOQ items, linked to a work; optionally attach multiple proposals.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              An estimate holds the detailed cost breakdown (BOQ) that will eventually flow into an admin sanction and a
              tender. Each work can have one <span className="font-medium">ORIGINAL</span> estimate plus any number of
              <span className="font-medium"> REVISED</span> / <span className="font-medium">SUPPLEMENTARY</span> estimates.
            </p>
          </div>
        )
      },
      {
        id: 'new-estimate',
        title: 'New Estimate flows',
        body: (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li><span className="font-medium">New Estimate</span> — blank form; pick a work via the Work Selector.</li>
            <li><span className="font-medium">Estimate For Existing Work</span> — pick a work first, then fill BOQ.</li>
            <li><span className="font-medium">Re-Estimate</span> — pick a work that already has an estimate; type defaults to REVISED.</li>
          </ul>
        )
      },
      {
        id: 'duplicate-guard',
        title: 'Duplicate guard',
        body: (
          <p className="text-sm text-gray-700">
            If the picked work already has an ORIGINAL estimate, the form surfaces them in a red banner and disables Save
            until you switch the type to <span className="font-medium">Revised</span> / <span className="font-medium">Supplementary</span>, or edit the existing one.
          </p>
        )
      },
      {
        id: 'multi-proposals',
        title: 'Attach multiple proposals (optional)',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Proposals are linked at the <span className="font-medium">work</span> level (many-to-many), so they travel
              with every estimate on that work.
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>In the estimate form find the <span className="font-medium">"Attached Proposals"</span> panel.</li>
              <li>Click <span className="font-medium">Attach Proposal</span>, search and multi-select.</li>
              <li>Use the × beside a row to detach.</li>
            </ol>
          </div>
        )
      },
      {
        id: 'dimensions',
        title: 'UDA / Scheme / Type / Category',
        body: (
          <p className="text-sm text-gray-700">
            These dropdowns are optional on the estimate itself. They're pre-filled from the work where possible but can be
            set independently — useful for orphan estimates that later become a new work.
          </p>
        )
      }
    ],
    related: ['wms.designs.create', 'wms.works.create-in-as'],
    pageLinks: [
      { label: 'Open Estimates', to: '/app/ds-wms/estimates', icon: 'ri-file-list-3-line' }
    ],
    launchForms: [
      { label: 'New Estimate', icon: 'ri-add-line', formKey: 'EstimateForm' }
    ]
  },
  {
    id: 'wms.designs.create',
    title: 'Create a Design',
    icon: 'ri-pencil-ruler-2-line',
    summary: 'One design per work; upload drawings, manage stages, attach proposals.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p className="text-sm text-gray-700">
            Each work has one active design with versioning. Designs progress through stages: PROPOSED → UNDER_DESIGN →
            SUBMITTED → UNDER_REVIEW → APPROVED → GROUNDED.
          </p>
        )
      },
      {
        id: 'new-design',
        title: 'Create',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>From the Designs page click <span className="font-medium">New Design</span>.</li>
            <li>Select a work in the Work Selector.</li>
            <li>Fill design name / remarks / estimate value and upload drawings.</li>
            <li>Save. The design starts in PROPOSED → DRAFT.</li>
          </ol>
        )
      },
      {
        id: 'duplicate-guard',
        title: 'One design per work',
        body: (
          <p className="text-sm text-gray-700">
            If the picked work already has a design, the form shows a red banner listing it and disables Save. Open the
            existing design to edit instead.
          </p>
        )
      },
      {
        id: 'multi-proposals',
        title: 'Attach multiple proposals (optional)',
        body: (
          <p className="text-sm text-gray-700">
            Same as estimates — the <span className="font-medium">Attached Proposals</span> panel is live once a work is
            selected. Proposals link at the work level so they are shared with the estimate.
          </p>
        )
      }
    ],
    pageLinks: [
      { label: 'Open Designs', to: '/app/ds-wms/designs', icon: 'ri-pencil-ruler-2-line' }
    ],
    launchForms: [
      { label: 'New Design', icon: 'ri-add-line', formKey: 'DesignForm' }
    ]
  }
];
